import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

const sections = [
  {
    title: 'Definitions',
    body: [
      ['Company', "means GS Pacific Marketing Aust Pty Ltd and includes its successors and assigns."],
      ['Conditions', "means these terms and conditions for the supply of Goods and/or Services by the Supplier to the Company."],
      ['Goods', "means the products, materials, supplies, equipment and other goods the subject of an Order."],
      ['Services', "means all things to be performed in accordance with an Order."],
      ['Supplier', "means the party supplying Goods and/or Services to the Company."],
      ['Food Standards Code', "means the Australia New Zealand Food Standards Code."],
      ['Other Relevant Requirements', "means all applicable laws, regulations, codes, standards and industry requirements relating to the Goods and Services."],
    ],
  },
  {
    title: 'Orders', paragraphs: [
      "The Conditions apply to and govern all Orders placed by the Company.",
      "No order is valid unless issued on the Company's official Purchase Order Form.",
      "These Conditions constitute the entire agreement between the Company and the Supplier unless otherwise agreed in writing.",
    ],
  },
  {
    title: 'Supply of Goods and Services',
    lead: 'Goods and Services supplied pursuant to an Order must:',
    bullets: [
      'comply with all specifications and requirements of the Order;',
      'be fit for purpose and of merchantable quality;',
      'comply with the Food Standards Code and all Other Relevant Requirements;',
      'comply with HACCP requirements where applicable;',
      'be delivered or performed by the time specified in the Order; and',
      'pass inspections and tests required by the Company.',
    ],
  },
  { title: 'Price', paragraphs: ["Unless otherwise agreed in writing, prices remain fixed for the duration of the Order and include all duties, taxes, GST, freight, packaging and delivery costs."] },
  { title: 'Delivery', paragraphs: ["Time is of the essence. The Supplier must deliver Goods and perform Services at the Site specified in the Order."] },
  { title: 'Title, Risk, Inspection and Acceptance', paragraphs: [
    "Title and risk in the Goods pass to the Company only after delivery, inspection and acceptance by the Company.",
    "The Company may reject any Goods or Services not complying with the Order or these Conditions.",
  ] },
  { title: 'Insurance', paragraphs: ["The Supplier must maintain adequate insurance coverage, including product liability and product recall insurance where applicable."] },
  { title: 'Indemnity', lead: 'The Supplier indemnifies the Company against all claims, losses, damages, liabilities and expenses arising from:', bullets: [
    'breach of these Conditions;',
    'negligence of the Supplier; or',
    'defective Goods or Services.',
  ] },
  { title: 'Laws and Other Requirements', paragraphs: ["The Supplier must comply with all applicable laws, licences, permits, HACCP requirements and lawful directions of the Company."] },
  { title: 'Warranty', paragraphs: [
    "The Supplier warrants that all Goods and Services comply with the Order and applicable laws and are free from defects.",
    "All Goods must be appropriately packed and traceable to raw materials used.",
  ] },
  { title: 'Confidential Information', paragraphs: ["All confidential information disclosed to the Supplier by the Company must remain confidential and may not be disclosed without prior written consent except as required by law."] },
  { title: 'Discontinuance of Supplier', paragraphs: ["The Supplier must provide at least three (3) months written notice before ceasing supply of Goods or Services where no fixed term applies."] },
  { title: 'Payment', paragraphs: [
    "The Supplier may invoice the Company following supply of Goods or completion of Services unless otherwise agreed in writing.",
    "Unless otherwise agreed, payment will be made within thirty (30) days after the end of the month in which a valid invoice is received.",
  ] },
  { title: 'Recovery Costs', paragraphs: [
    "The Supplier acknowledges and agrees that if any amount payable to the Company becomes overdue, GS Pacific Marketing Aust Pty Ltd reserves the right, at its discretion, to refer the outstanding account to a mercantile agency or debt collection agency for recovery.",
    "The Supplier agrees that all costs, commissions, fees and charges incurred by the Company in connection with the recovery of the overdue amount, including any costs charged by a mercantile agency or debt collection agency (as if the account had been collected by that agency), will be added to and form part of the outstanding debt payable by the Supplier.",
    "The Supplier further agrees that any legal costs incurred by the Company in recovering the debt, including solicitor-client costs and disbursements, will be recoverable on a full indemnity basis and will form part of the debt due and payable to the Company.",
  ] },
  { title: 'Cancellation', paragraphs: ["The Company may cancel any Order upon written notice and will only be liable for Goods delivered or Services completed up to the cancellation date."] },
  { title: 'Termination for Default', paragraphs: ["The Company may terminate an Order or Contract immediately if the Supplier breaches these Conditions, becomes insolvent or fails to perform its obligations."] },
  { title: 'Law Applicable', paragraphs: ["These Conditions are governed by the laws of New South Wales, Australia."] },
  { title: 'Assignment', paragraphs: ["The Supplier must not assign or subcontract any Order or Contract without prior written consent from the Company."] },
  { title: 'Intellectual Property Rights', paragraphs: ["The Supplier indemnifies the Company against all claims relating to infringement of intellectual property rights arising from the Goods or Services."] },
  { title: 'Liens', paragraphs: ["The Supplier must not claim any lien or similar interest over Goods or Services supplied to the Company."] },
  { title: 'Waivers', paragraphs: ["Any waiver by the Company must be in writing and does not waive any other rights under these Conditions."] },
  { title: 'Dispute Resolution', paragraphs: ["Any dispute not resolved within fourteen (14) days may be referred to an independent expert whose determination will be final and binding."] },
];

const Section = ({ n, title, lead, paragraphs, bullets, body }) => (
  <section
    data-testid={`terms-section-${n}`}
    className="break-inside-avoid mb-6"
  >
    <h2 className="text-sm md:text-base font-semibold text-[#01195d] mb-2">
      <span className="text-slate-400 font-normal mr-1.5">{n}.</span>{title}
    </h2>
    {lead && <p className="text-slate-700 text-xs md:text-[13px] mb-2 leading-relaxed">{lead}</p>}
    {paragraphs?.map((p, i) => (
      <p key={i} className="text-slate-700 text-xs md:text-[13px] mb-2 leading-relaxed">{p}</p>
    ))}
    {bullets && (
      <ul className="list-disc pl-5 space-y-1 text-slate-700 text-xs md:text-[13px]">
        {bullets.map((b, i) => <li key={i} className="leading-relaxed">{b}</li>)}
      </ul>
    )}
    {body && (
      <dl className="space-y-1.5">
        {body.map(([term, def], i) => (
          <div key={i} className="text-slate-700 text-xs md:text-[13px] leading-relaxed">
            <dt className="inline font-semibold text-slate-900">{term}</dt>
            <dd className="inline"> {def}</dd>
          </div>
        ))}
      </dl>
    )}
  </section>
);

export const TermsPage = () => {
  return (
    <div data-testid="terms-page" className="bg-slate-50 min-h-screen">
      {/* Hero */}
      <div className="bg-[#01195d] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
          <Link
            to="/"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-1 text-slate-300 hover:text-white text-sm mb-5 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Back to Home
          </Link>
          <p className="text-xs uppercase tracking-widest text-slate-300 mb-2">Legal</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
            Standard Terms and Conditions of Purchase for Goods
          </h1>
          <p className="text-slate-300 mt-3 text-sm md:text-base">
            By GS Pacific Marketing Aust Pty Ltd
          </p>
        </div>
      </div>

      {/* Content - 2 column layout like the PDF */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5 md:p-10">
          <div className="md:columns-2 md:gap-10 [column-rule:1px_solid_#e2e8f0]">
            {sections.map((s, i) => (
              <Section key={i} n={i + 1} {...s} />
            ))}
          </div>

          <div className="pt-6 mt-4 border-t border-slate-200 text-xs text-slate-500">
            <p>GS Pacific Marketing Aust Pty Ltd · ABN 51 114 918 366</p>
            <p>Unit 1, 2 Costello Place, Seven Hills NSW 2147 · 1800 934 114 · info@gspacific.com.au</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
